'use client';

import { useMemo, useState } from 'react';

import {
  canAddDirectly,
  sortFavorites,
  useFavoriteProducts,
  type FavoritesSort as Sort,
} from '@eliadmin/core/src/components/nt/account/favorites/favorites-data';
import { FavoritesSort } from '@eliadmin/core/src/components/nt/account/favorites/favorites-sort';
import { FavoritesStateCard } from '@eliadmin/core/src/components/nt/account/favorites/favorites-state-card';
import { useAddAllToCart } from '@eliadmin/core/src/components/nt/account/favorites/use-add-all-to-cart';
import { AccountHeading } from '@eliadmin/core/src/components/nt/account/shell/account-card';
import { ProductTile } from '@eliadmin/core/src/components/nt/product/product-tile';
import {
  useAccountTexts,
  useDrawerTexts,
} from '@eliadmin/core/src/components/nt/shared/site-texts';
import Link from '@eliadmin/core/src/components/link';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Skeleton } from '@eliadmin/core/src/components/ui/skeleton';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import {
  fillTemplate,
  pluralText,
  splitOptions,
  stripTags,
} from '@eliadmin/core/src/utils/nt/text';

/** Figma „Grid“: 3 × 314 px, gap 24 × 32 in the 990 px column; 2 columns from 30rem, 1 below. */
const GRID =
  'grid grid-cols-1 gap-x-4 gap-y-8 @[30rem]:grid-cols-2 @[52rem]:grid-cols-3 @[52rem]:gap-x-6';
const TILE_SIZES =
  '(min-width: 1392px) 314px, (min-width: 1024px) calc((100vw - 400px) / 3), (min-width: 640px) 45vw, 100vw';

/**
 * „Obľúbené produkty“ of a signed-in customer (Figma 208:12397) in the content column of the
 * account shell: heading with „{produkty} · zoznam je uložený…“, „Pridať všetko do košíka“ and
 * „Zoradiť“, then the `ProductTile` grid. The heart of a tile removes the favourite (the list
 * refetches). Figma's „Alert · Zľava“ („Na 2 obľúbené tonery klesla cena…“) is not rendered: the API
 * keeps no price history of a favourite and the administration has no text for it.
 */
export function FavoritesView() {
  const { t } = useTranslations();
  const texts = useAccountTexts();
  // „{pocet} produkt | … produktov“ is a cart text; the header hydrates it on every page.
  const cartTexts = useDrawerTexts();
  const isWholesaler = useCustomerStore(state => state.me?.customer.is_wholesaler);
  const priceWithoutVatFirst = useGlobalStore(
    state => state.templateSettings.product_detail?.price_without_vat_first?.enabled,
  );
  const [sort, setSort] = useState<Sort>('recent');
  const favorites = useFavoriteProducts();
  const addAll = useAddAllToCart();

  const products = useMemo(
    () =>
      favorites.data
        ? sortFavorites(favorites.data, sort, { isWholesaler, priceWithoutVatFirst })
        : undefined,
    [favorites.data, sort, isWholesaler, priceWithoutVatFirst],
  );
  const addable = useMemo(() => products?.filter(canAddDirectly) ?? [], [products]);

  const count =
    products && cartTexts
      ? fillTemplate(stripTags(texts.favoritesText), {
          produkty: pluralText(stripTags(cartTexts.products), products.length),
        })
      : null;

  return (
    <div className="flex flex-col gap-4">
      <AccountHeading
        title={t('text61')}
        text={
          count ??
          (favorites.isError ? null : (
            // The count line keeps its place while the list loads (a span: the heading wraps it in <p>).
            <span
              aria-hidden="true"
              className="block h-[22px] w-2/3 animate-pulse rounded-[6px] bg-[#F5F6F3]"
            />
          ))
        }
      />

      {!products ? (
        favorites.isError ? (
          <FavoritesStateCard
            icon="warning-circle"
            title={t('text1472')}
            text={t('text1320')}
            actions={
              <Button variant="outline" size="md" onClick={() => void favorites.refetch()}>
                {t('text595')}
              </Button>
            }
          />
        ) : (
          <GridSkeleton />
        )
      ) : !products.length ? (
        <FavoritesStateCard
          icon="heart"
          title={t('text1381')}
          actions={
            <Button asChild variant="primary" size="md">
              <Link href={ROUTES.HOME() ?? '/'}>{t('text208')}</Link>
            </Button>
          }
        />
      ) : (
        <>
          <div className="flex flex-col items-stretch gap-4 @[30rem]:flex-row @[30rem]:items-center @[30rem]:justify-between">
            <Button
              variant="primary"
              size="md"
              iconLeft="shopping-cart-simple"
              isLoading={addAll.isPending}
              disabled={!addable.length}
              onClick={() => addAll.mutate(addable)}
            >
              {stripTags(texts.addAll)}
            </Button>
            <FavoritesSort
              labels={splitOptions(texts.favoritesSort)}
              value={sort}
              onChange={setSort}
              className="self-start @[30rem]:self-auto"
            />
          </div>
          <ul className={GRID} aria-busy={favorites.isFetching || undefined}>
            {products.map(product => (
              <li key={product.id} className="flex">
                <ProductTile product={product} imageSizes={TILE_SIZES} />
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/** Placeholder tiles while the list loads (one row of the grid). */
function GridSkeleton() {
  return (
    <ul aria-hidden="true" className={GRID}>
      {Array.from({ length: 3 }, (_, index) => (
        <li key={index} className="flex flex-col gap-4">
          <Skeleton className="aspect-square w-full rounded-2xl bg-[#F5F6F3]" />
          <Skeleton className="h-[42px] w-11/12 bg-[#F5F6F3]" />
          <Skeleton className="h-[22px] w-1/3 bg-[#F5F6F3]" />
        </li>
      ))}
    </ul>
  );
}
